// Draws the Account Statement (accountStatement.js) straight into a PDF as text.
// It does not photograph the screen, so it works while the page scrolls or
// re-renders, the text stays sharp, and long tables continue onto new pages
// with the column headings repeated.
import { jsPDF } from "jspdf";
import { formatInr } from "./accountStatement";

const BRAND = [34, 81, 130];
const TEXT = [24, 47, 83];
const MUTED = [100, 116, 139];
const BORDER = [226, 232, 240];
const TONE = { danger: [220, 38, 38], warning: [180, 83, 9], muted: MUTED };
const FILL = {
  charge: [248, 250, 252],
  deduction: [248, 250, 252],
  carried: [239, 246, 255],
  excess: [255, 247, 237],
  foreclosure: [236, 253, 245],
};

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 12;
const CONTENT_W = PAGE_W - MARGIN * 2;
const BOTTOM = PAGE_H - 22; // footer starts below this

const COLUMNS = [
  { title: "Date", width: 17, key: "collectionDate" },
  { title: "Due Date", width: 17, key: "scheduledDate" },
  { title: "Particulars", width: 58, key: "description" },
  { title: "Increased By", width: 18, key: "increasedBy", money: true },
  { title: "Decreased By", width: 18, key: "decreasedBy", money: true },
  { title: "Interest", width: 16, key: "interest", money: true },
  { title: "Principal", width: 18, key: "principal", money: true },
  { title: "Balance", width: 24, key: "balance", money: true },
];

const loadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const setText = (pdf, size, color = TEXT, style = "normal") => {
  pdf.setFont("helvetica", style);
  pdf.setFontSize(size);
  pdf.setTextColor(...color);
};

const drawFooters = (pdf, statement) => {
  const pages = pdf.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    pdf.setPage(i);
    const y = PAGE_H - 16;
    pdf.setDrawColor(...BORDER);
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN, y, PAGE_W - MARGIN, y);
    setText(pdf, 6.5, TEXT, "bold");
    pdf.text("VETRIVIKAS CAPITAL TRUST", MARGIN, y + 4.5);
    setText(pdf, 6, MUTED);
    pdf.text(
      "Regd. Office: Flat No.25, Arunachaleshwarar 3rd Cross Street, HMS Colony 1st Main Road, Kokkalappi, Madurai, Tamil Nadu - 625016. Office: 0452-490 9438",
      MARGIN,
      y + 8
    );
    pdf.text(`${statement.agreementNo} | Page ${i} of ${pages}`, PAGE_W - MARGIN, y + 4.5, { align: "right" });
  }
};

const drawTableHeader = (pdf, y) => {
  pdf.setFillColor(...BRAND);
  pdf.rect(MARGIN, y, CONTENT_W, 7, "F");
  setText(pdf, 6.6, [255, 255, 255], "bold");
  let x = MARGIN;
  COLUMNS.forEach((col) => {
    if (col.money) pdf.text(col.title, x + col.width - 1.5, y + 4.6, { align: "right" });
    else pdf.text(col.title, x + 1.5, y + 4.6);
    x += col.width;
  });
  return y + 7;
};

export const downloadAccountStatementPdf = async (statement, logoSrc) => {
  const pdf = new jsPDF("p", "mm", "a4");
  let y = MARGIN;

  // Heading: logo, title, agreement and generated time.
  const logo = logoSrc ? await loadImage(logoSrc) : null;
  if (logo && logo.naturalWidth) {
    const h = 14;
    pdf.addImage(logo, "JPEG", MARGIN, y, (logo.naturalWidth / logo.naturalHeight) * h, h);
  }
  setText(pdf, 15, BRAND, "bold");
  pdf.text("Account Statement", PAGE_W - MARGIN, y + 5, { align: "right" });
  setText(pdf, 8, MUTED);
  pdf.text(`${statement.productLabel}  |  Agreement No. ${statement.agreementNo}`, PAGE_W - MARGIN, y + 10, { align: "right" });
  pdf.text(`Generated on ${statement.generatedOn}`, PAGE_W - MARGIN, y + 14, { align: "right" });
  y += 18;
  pdf.setDrawColor(...BRAND);
  pdf.setLineWidth(0.8);
  pdf.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 5;

  // Totals.
  const tiles = [
    ["Total Overall Debt", statement.totals.debt],
    ["Total Paid", statement.totals.paid],
    ["Remaining To Pay", statement.totals.remaining],
    ["Overdue Now", statement.totals.overdue],
  ];
  const gap = 3;
  const tileW = (CONTENT_W - gap * (tiles.length - 1)) / tiles.length;
  tiles.forEach(([label, value], i) => {
    const x = MARGIN + i * (tileW + gap);
    const highlight = i === 2;
    pdf.setDrawColor(...(highlight ? BRAND : BORDER));
    pdf.setFillColor(...(highlight ? BRAND : [255, 255, 255]));
    pdf.setLineWidth(0.3);
    pdf.roundedRect(x, y, tileW, 15, 2, 2, highlight ? "FD" : "S");
    setText(pdf, 6.5, highlight ? [226, 232, 240] : MUTED, "bold");
    pdf.text(label.toUpperCase(), x + 3, y + 5);
    setText(pdf, 12, highlight ? [255, 255, 255] : TEXT, "bold");
    pdf.text(`Rs.${formatInr(value)}`, x + 3, y + 11.5);
  });
  y += 21;

  // Customer, loan and repayment details in three columns.
  const colW = (CONTENT_W - 8) / 3;
  let detailsBottom = y;
  statement.sections.forEach((section, i) => {
    const x = MARGIN + i * (colW + 4);
    let cy = y;
    setText(pdf, 7.5, BRAND, "bold");
    pdf.text(section.title.toUpperCase(), x, cy + 3);
    cy += 5.5;
    section.items.forEach((item) => {
      setText(pdf, 6.3, MUTED);
      pdf.text(item.label, x, cy + 2.5);
      setText(pdf, 7.2, TEXT, "bold");
      const lines = pdf.splitTextToSize(String(item.value ?? "-"), colW);
      pdf.text(lines, x, cy + 6);
      cy += 4.5 + lines.length * 3.2;
    });
    detailsBottom = Math.max(detailsBottom, cy);
  });
  y = detailsBottom + 4;

  // Transactions.
  y = drawTableHeader(pdf, y);
  let lastGroup = null;
  const groupTitles = { charges: "Loan amount and charges", repayments: "Repayments" };
  statement.rows.forEach((row) => {
    const descLines = pdf.splitTextToSize(row.description, COLUMNS[2].width - 3);
    const noteLines = row.note ? pdf.splitTextToSize(row.note, COLUMNS[2].width - 3) : [];
    const groupHeight = row.group !== lastGroup ? 6 : 0;
    const h = 2.6 + descLines.length * 3 + noteLines.length * 2.8 + 1.4;
    if (y + h + groupHeight > BOTTOM) {
      pdf.addPage();
      y = MARGIN;
      setText(pdf, 7, MUTED);
      pdf.text(`Account Statement - ${statement.customerName} - ${statement.agreementNo} (continued)`, MARGIN, y + 3);
      y = drawTableHeader(pdf, y + 5);
    }
    if (row.group !== lastGroup) {
      pdf.setFillColor(241, 245, 249);
      pdf.rect(MARGIN, y, CONTENT_W, 6, "F");
      setText(pdf, 6.6, BRAND, "bold");
      pdf.text(groupTitles[row.group].toUpperCase(), MARGIN + 1.5, y + 4);
      y += 6;
      lastGroup = row.group;
    }
    if (FILL[row.kind]) {
      pdf.setFillColor(...FILL[row.kind]);
      pdf.rect(MARGIN, y, CONTENT_W, h, "F");
    }
    const isDue = row.kind === "due";
    let x = MARGIN;
    COLUMNS.forEach((col) => {
      const value = row[col.key];
      if (col.key === "description") {
        setText(pdf, 7, isDue ? MUTED : TEXT, isDue ? "italic" : row.kind === "foreclosure" ? "bold" : "normal");
        pdf.text(descLines, x + 1.5, y + 3.6);
        if (noteLines.length) {
          setText(pdf, 6.2, TONE[row.noteTone] || MUTED);
          pdf.text(noteLines, x + 1.5, y + 3.6 + descLines.length * 3);
        }
      } else if (col.money) {
        const show = value !== null && value !== undefined && value !== "";
        setText(pdf, 7, isDue ? MUTED : TEXT, col.key === "balance" && !isDue ? "bold" : "normal");
        pdf.text(show ? formatInr(value) : "-", x + col.width - 1.5, y + 3.6, { align: "right" });
      } else {
        setText(pdf, 7, isDue ? MUTED : TEXT);
        pdf.text(value || "-", x + 1.5, y + 3.6);
      }
      x += col.width;
    });
    pdf.setDrawColor(...BORDER);
    pdf.setLineWidth(0.2);
    pdf.line(MARGIN, y + h, PAGE_W - MARGIN, y + h);
    y += h;
  });

  // Foreclosure settlement, for reference (not part of the running balance).
  if (statement.foreclosureSettlement) {
    const items = statement.foreclosureSettlement;
    const boxW = 95;
    const boxH = 8 + items.length * 5;
    y += 4;
    if (y + boxH > BOTTOM) {
      pdf.addPage();
      y = MARGIN;
    }
    pdf.setDrawColor(...BORDER);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(MARGIN, y, boxW, boxH, 2, 2, "S");
    setText(pdf, 7.5, BRAND, "bold");
    pdf.text("FORECLOSURE SETTLEMENT", MARGIN + 3, y + 5);
    let iy = y + 10;
    items.forEach((item) => {
      const color = item.tone === "total" ? BRAND : item.tone === "waiver" ? TONE.danger : TEXT;
      setText(pdf, 7, item.tone ? color : MUTED, item.tone === "total" ? "bold" : "normal");
      pdf.text(item.label, MARGIN + 3, iy);
      setText(pdf, 7, color, "bold");
      pdf.text(String(item.value), MARGIN + boxW - 3, iy, { align: "right" });
      iy += 5;
    });
    y += boxH + 5;
  }

  // Final balance.
  if (y + 9 > BOTTOM) {
    pdf.addPage();
    y = MARGIN;
  }
  pdf.setFillColor(232, 239, 247);
  pdf.rect(MARGIN, y, CONTENT_W, 8, "F");
  setText(pdf, 8.5, BRAND, "bold");
  pdf.text("Final Balance Owed", MARGIN + 1.5, y + 5.4);
  pdf.text(`Rs.${formatInr(statement.finalBalance)}`, PAGE_W - MARGIN - 1.5, y + 5.4, { align: "right" });
  y += 13;

  if (y + 6 > BOTTOM) {
    pdf.addPage();
    y = MARGIN;
  }
  setText(pdf, 7, MUTED, "bold");
  pdf.text("*** END OF STATEMENT ***", PAGE_W / 2, y, { align: "center" });

  drawFooters(pdf, statement);
  pdf.setProperties({
    title: `Account Statement - ${statement.agreementNo}`,
    subject: `${statement.productLabel} account statement`,
    author: "Vetrivikas Capital Trust",
    creator: "Vetrivikas Capital Trust",
  });
  pdf.save(`${statement.customerName || "Customer"} - ${statement.agreementNo} - Account Statement.pdf`);
};
